import * as React from 'react'
import { FunctionComponent, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import HammockDialog, { HammockDialogProps } from './HammockDialog'
import { Formik, FormikHelpers } from 'formik'
import Form from '../../../components/Form/Form'
import { FormikConfig } from 'formik/dist/types'
import { Schema } from 'yup'
import { normalizeAxiosError, NormalizedError } from '../../../reducers/errorReducer'
import { useFormikContext } from 'formik'

export type HammockFormDialogProps<E, DialogBodyPropsT = unknown> = Omit<HammockDialogProps<DialogBodyPropsT>, 'dialogBody' | 'dialogBodyProps'> & {
    dialogFormProps?: () => FormikConfig<E>
    dialogFormBody: FunctionComponent<HammockFormDialogProps<E, DialogBodyPropsT>>
    dialogFormBodyProps?: () => DialogBodyPropsT
    setOnSubmit?: (onSubmit: FormikConfig<E>['onSubmit']) => void
    setValidationSchema?: (schema: FormikConfig<E>['validationSchema']) => void
    /**
     * Opt in to keeping the dialog open when Escape or a click next to it would throw away unsaved changes. Off by
     * default, so existing dialogs keep closing the way they always did (HAM-4275).
     */
    warnOnUnsavedChanges?: boolean
    /** Set by HammockFormDialog: reports whether the form has unsaved changes. */
    onDirtyChange?: (dirty: boolean) => void
    /**
     * True when the user tried to close the dialog with Escape or a click next to it while the form had unsaved
     * changes. The dialog stayed open; the body shows a message about it, which beats a second dialog on top of this
     * one (HAM-4275).
     */
    unsavedWarning?: boolean
}

/** Reports whether the surrounding form has unsaved changes. */
function DirtyTracker(props: { onChange: (dirty: boolean) => void }): ReactElement {
    const { dirty, isSubmitting } = useFormikContext()

    useEffect(() => {
        props.onChange(dirty && !isSubmitting)
        return () => props.onChange(false)
    }, [dirty, isSubmitting])

    return <></>
}

/**
 * A little more complex Dialog to support a Form. The DialogSurface will contain a Formik form and provides for an onSubmit + validation.
 *
 * @param props all optional props, except dialogBody and dialogFormBody.
 */
function HammockFormDialogBody<E, DialogBodyPropsT = unknown>(props: HammockFormDialogProps<E, DialogBodyPropsT>): ReactElement {
    const dialogFormProps = useMemo(() => props.dialogFormProps?.() ?? ({} as FormikConfig<E>), [props])

    const localOnSubmitRef = useRef<FormikConfig<E>['onSubmit']>(dialogFormProps.onSubmit)

    const [globalErrors, setGlobalErrors] = useState<NormalizedError[]>()
    const [validationSchema, setValidationSchema] = useState<Schema>(dialogFormProps.validationSchema)

    return (
        <Formik<E>
            validateOnChange={false}
            validateOnBlur={false}
            {...dialogFormProps}
            onSubmit={(values: E, formikHelpers: FormikHelpers<E>) => {
                let localOnSubmit = dialogFormProps.onSubmit
                if (localOnSubmitRef.current) {
                    localOnSubmit = localOnSubmitRef.current
                }

                return Promise.resolve()
                    .then(() => localOnSubmit?.(values, formikHelpers))
                    .then(
                        (keepOpen: boolean) => keepOpen !== true && props.onDismiss?.(),
                        e => setGlobalErrors([normalizeAxiosError(e)]),
                    )
            }}
            validationSchema={validationSchema}
        >
            <Form globalErrors={globalErrors}>
                <DirtyTracker onChange={dirty => props.onDirtyChange?.(dirty)} />
                <props.dialogFormBody
                    {...props}
                    setOnSubmit={(fn: FormikConfig<E>['onSubmit']) => (localOnSubmitRef.current = fn)}
                    setValidationSchema={(schema: FormikConfig<E>['validationSchema']) => setValidationSchema(schema)}
                />
            </Form>
        </Formik>
    )
}

export default function HammockFormDialog<E, DialogBodyPropsT = unknown>(props: HammockFormDialogProps<E, DialogBodyPropsT>): ReactElement {
    const dirty = useRef<boolean>(false)
    const [unsavedWarning, setUnsavedWarning] = useState(false)

    /**
     * With warnOnUnsavedChanges, Escape and a click next to the dialog stop working while the form has unsaved changes;
     * the dialog stays open and says so. Saving and Cancel run through onDismiss and are never affected - Cancel is the
     * deliberate way out.
     */
    const setOpen = (open: boolean) => {
        if (!open && props.warnOnUnsavedChanges && dirty.current) {
            setUnsavedWarning(true)
            return
        }

        setUnsavedWarning(false)
        props.setOpen?.(open)
    }

    return (
        <HammockDialog<DialogBodyPropsT>
            {...props}
            setOpen={setOpen}
            onDirtyChange={(value: boolean) => {
                dirty.current = value
                if (!value) {
                    setUnsavedWarning(false)
                }
            }}
            unsavedWarning={unsavedWarning}
            dialogBody={HammockFormDialogBody as any}
        />
    )
}
